import { UzButton, UzDialog, UzSpinner } from '@utazzo/ui';
import { Shuffle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { canBeAssigned } from '../labels';
import { useBulkAssignLpsLeads, useLpsLeadAgents } from '../use-lps';

import { LpsAgentPicker } from './AssignAgentDialog';

import type { LpsLeadItem } from '../lps.api';

import { useCurrentUser } from '@/features/auth/use-auth';
import { useAgentOptionsForLeads } from '@/features/leads/use-lead-options';

/** Already in the CRM (round-robin or an earlier pick): reassigned there. */
const isInCrm = (r: LpsLeadItem): boolean =>
  r.forward.status === 'FORWARDED' && r.forward.crmLeadId !== null;

/**
 * Assign the grid's selection in one go - filter (say Destination = Kerala),
 * tick the rows, pick an agent. Mirrors the Unassigned page's bulk assign.
 * Rows waiting for an agent are sent to the CRM under the pick; rows already
 * in the CRM move to the pick there (a manual override of round-robin). Rows
 * mid-send are left alone and counted in the note.
 */
export const BulkAssignAgentDialog = ({
  rows,
  onClose,
}: {
  rows: ReadonlyArray<LpsLeadItem> | null;
  onClose: () => void;
}): React.JSX.Element => {
  const waitingRows = useMemo(() => (rows ?? []).filter(canBeAssigned), [rows]);
  const inCrmRows = useMemo(() => (rows ?? []).filter(isInCrm), [rows]);
  const assignable = useMemo(() => [...waitingRows, ...inCrmRows], [waitingRows, inCrmRows]);
  const skipped = (rows?.length ?? 0) - assignable.length;
  const hasDuplicates = waitingRows.some((r) => r.status === 'DUPLICATE');
  // The picker ranks experts for one lead's destination; with a destination
  // filter on, every row shares it, so the first lead stands in for all.
  const agents = useLpsLeadAgents(assignable[0]?.id ?? null);
  const assign = useBulkAssignLpsLeads();
  const [picked, setPicked] = useState<string | null>(null);
  // A sales lead may only pick themselves or a direct report (the API refuses
  // anyone else). The lead-form roster is already scoped that way server-side.
  const isSalesLead = useCurrentUser()?.role === 'SALES_LEAD';
  const team = useAgentOptionsForLeads();
  const teamIds = useMemo(
    () => (isSalesLead ? new Set(team.options.map((o) => o.value)) : null),
    [isSalesLead, team.options],
  );

  // Sent so far / in this run - the request goes out in chunks, and
  // round-robin sends fewer leads than a pick (waiting ones only).
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    setPicked(null);
    setProgress({ done: 0, total: 0 });
    assign.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const count = assignable.length;
  const isSaving = assign.isPending;
  const failed = assign.data?.failed ?? [];
  const nameOf = new Map(assignable.map((r) => [r.id, r.fullName ?? r.phone ?? r.id]));

  const run = async (userId: string | null) => {
    // Round-robin only deals out leads nobody holds yet; a pick moves them all.
    const ids = (userId ? assignable : waitingRows).map((r) => r.id);
    if (ids.length === 0) return;
    try {
      const res = await assign.mutateAsync({
        ids,
        userId,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      if (res.failed.length === 0) onClose();
    } catch {
      // Toast already shown; keep the dialog open.
    }
  };

  return (
    <UzDialog
      open={rows !== null}
      // Locked while sending: closing mid-batch would hide whether it landed.
      forceOpen={isSaving}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={`Assign ${count} lead${count === 1 ? '' : 's'}`}
      description={
        agents.data?.destination
          ? `Destination matched: ${agents.data.destination.name}. Experts are listed first.`
          : 'Pick an agent for every selected lead.'
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <UzButton
            variant="outline"
            leftIcon={<Shuffle className="size-4" />}
            disabled={isSaving || waitingRows.length === 0}
            onClick={() => void run(null)}
            tooltip={
              waitingRows.length === 0
                ? 'Every selected lead already has an agent - pick one to reassign'
                : 'Give each lead nobody holds yet to the next expert in turn'
            }
          >
            Assign by round-robin
          </UzButton>
          <div className="flex gap-2">
            <UzButton variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </UzButton>
            <UzButton
              disabled={!picked || count === 0}
              loading={isSaving}
              onClick={() => void run(picked)}
            >
              {isSaving ? 'Assigning…' : 'Assign all and send to CRM'}
            </UzButton>
          </div>
        </div>
      }
    >
      {inCrmRows.length > 0 && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {inCrmRows.length} of these {inCrmRows.length === 1 ? 'is' : 'are'} already in the CRM
          with an agent. Picking an agent below moves {inCrmRows.length === 1 ? 'it' : 'them'} to
          that agent; round-robin leaves {inCrmRows.length === 1 ? 'it' : 'them'} where{' '}
          {inCrmRows.length === 1 ? 'it is' : 'they are'}.
        </p>
      )}
      {skipped > 0 && (
        <p className="mb-3 rounded-md bg-zinc-100 px-3 py-2 text-xs text-zinc-700">
          {skipped} selected lead{skipped === 1 ? ' is' : 's are'} being sent to the CRM right now,
          so {skipped === 1 ? 'it is' : 'they are'} left out. Try again in a minute.
        </p>
      )}
      {hasDuplicates && (
        <p className="mb-3 rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">
          Some of these are repeat enquiries. Assigning them creates new CRM leads under the same
          customers.
        </p>
      )}
      {isSaving && (
        <div className="mb-3 rounded-md border border-blue-200 bg-blue-50/60 px-4 py-3">
          <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 font-medium text-zinc-800">
              <UzSpinner size="xs" tone="primary" />
              Assigning leads…
            </span>
            <span className="text-zinc-600 tabular-nums">
              {progress.done.toLocaleString('en-IN')} / {progress.total.toLocaleString('en-IN')}
            </span>
          </div>
          {/* Fills per chunk sent; the shimmer track shows under the fill. */}
          <div className="skel h-2 w-full overflow-hidden rounded-full">
            <div
              className="h-full rounded-full bg-blue-600 transition-[width]"
              style={{
                width: `${progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0}%`,
              }}
            />
          </div>
          <div className="mt-1.5 text-[11px] text-zinc-500">
            Please keep this window open until the assignment finishes.
          </div>
        </div>
      )}
      {!isSaving && failed.length > 0 && (
        <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
          <p className="font-medium">
            {assign.data!.assigned} assigned, {failed.length} failed:
          </p>
          <ul className="mt-1 list-disc pl-4">
            {failed.map((f) => (
              <li key={f.id}>
                {nameOf.get(f.id) ?? f.id}: {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}
      {count > 0 ? (
        <LpsAgentPicker agents={agents} picked={picked} onPick={setPicked} onlyIds={teamIds} />
      ) : (
        <p className="py-6 text-center text-sm text-zinc-500">
          None of the selected leads can be assigned right now.
        </p>
      )}
    </UzDialog>
  );
};
